import { createStyles, Divider, Stack } from '@mantine/core';
import { Text } from '@repo/foundations';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { formatDateTimeWithAt } from '../../../../../utils/time';
import { PiiClassifier } from '../PiiClassifier';
import { PolicyDataSamples } from '../PolicyDataSamples';
import policyIssueStore from '../store';
import { ViolationStatusBadge } from '../ViolationStatusBadge';
import { IssueActionBar } from './IssueActionBar';
import IssueActivity from './IssueActivity';
import IssueSection from './IssueSection';

interface PolicyIssueIssueContentProps {
	handleAcknowledgeOnClick: (resultId: string) => void;
	handleResolveOnClick: (resultId: string) => void;
	handleIgnoreOnClick: (resultId: string) => void;
	handleReopenOnClick: (resultId: string) => void;
	readOnly?: boolean;
}

const useStyles = createStyles((theme) => ({
	root: {
		background: theme.other.getColor('surface/app/default'),
		height: '-webkit-fill-available',
	},
}));

export const PolicyIssueIssueContent = observer(
	({
		handleAcknowledgeOnClick,
		handleResolveOnClick,
		handleIgnoreOnClick,
		handleReopenOnClick,
		readOnly = false,
	}: PolicyIssueIssueContentProps) => {
		const { violations } = policyIssueStore;
		const { classes } = useStyles();
		const lastIssueOccurred = formatDateTimeWithAt(
			violations?.[0]?.created_at ?? null
		);
		const dataSamples = useMemo(
			() =>
				violations
					?.map((violation) => violation.data_samples)
					.flat()
					.filter(Boolean)
					.filter((sample) => !isNil(sample)),
			[violations]
		);

		const isPiiScan = useMemo(
			() =>
				violations?.some(
					(violation) => violation.policy_rule.type === 'PII_SCAN'
				),
			[violations]
		);

		return (
			<Stack spacing={0} className={classes.root}>
				<IssueActionBar
					violations={violations ?? []}
					handleAcknowledgeOnClick={handleAcknowledgeOnClick}
					handleResolveOnClick={handleResolveOnClick}
					handleIgnoreOnClick={handleIgnoreOnClick}
					handleReopenOnClick={handleReopenOnClick}
					readOnly={readOnly}
				/>
				<Stack p="xl" pt="md" spacing="sm">
					<IssueSection
						label="Issue status"
						value={<ViolationStatusBadge violations={violations ?? []} />}
					/>
					{isPiiScan && (
						<IssueSection
							label="PII classifier"
							value={<PiiClassifier violations={violations ?? []} />}
						/>
					)}
					{isPiiScan && (
						<IssueSection
							label="Data samples"
							value={
								<PolicyDataSamples
									dataSamples={dataSamples ?? []}
									displayLimit={3}
								/>
							}
						/>
					)}
					<IssueSection
						label="Last issue occurred"
						value={<Text size="sm">{lastIssueOccurred}</Text>}
					/>
				</Stack>
				<Divider />
				{violations?.[0] && <IssueActivity policyResult={violations?.[0]} />}
				<Divider />
			</Stack>
		);
	}
);
